import { useApplicationRuntimeContext } from '@context/ApplicationRuntimeContext';

import { useFormattedPrice } from '../useFormattedPrice';
import { useFormattedSecondaryPrice } from '../useFormattedSecondaryPrice';

import { IPrices } from './model';

/**
 * This hook initializes PrimaryPrice and SecondaryPrice based on input and state from Application context
 * It handles price formatting for both currencies and returns the currencies in correct order.
 * @param price - Price which is used for both currencies calculations
 * @param dualPriceEnabled - Override that will disable currency order switches
 *
 * - if dualPriceEnabled parameter is set to false, primary currency is always returned as first value (primaryPrice).
 * - This behaviour is mainly for components that display only primary currency,
 * in such a cases we can NOT manipulate the order since primary currency could end up not being displayed
 */
export const useInitializePrices = (price: number, dualPriceEnabled: boolean): IPrices => {
  const {
    applicationSettings: {
      features: { isPrimaryCurrencyFirst },
    },
  } = useApplicationRuntimeContext();
  const primaryCurrencyPrice = useFormattedPrice(price);
  const secondaryCurrencyPrice = useFormattedSecondaryPrice(price);

  if (secondaryCurrencyPrice && isPrimaryCurrencyFirst && dualPriceEnabled) {
    return { firstPrice: primaryCurrencyPrice, secondPrice: secondaryCurrencyPrice };
  }

  return { firstPrice: secondaryCurrencyPrice, secondPrice: primaryCurrencyPrice };
};
